import { zodResolver } from "@hookform/resolvers/zod";
import type { GetServerSideProps } from "next";
import { signIn, useSession } from "next-auth/react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Layout } from "~/components/Layout/Layout";
import { EventNames } from "~/mixpanel/EventNames";
import { trackEvent } from "~/mixpanel/trackEvent";

const loginFormData = z.object({
  email: z.string().email({ message: "Email is required" }),
  password: z.string().min(1, { message: "Password is required" }),
});

type loginFormData = z.infer<typeof loginFormData>;

function SignIn() {
  const [credentialsError, setCredentialsError] = useState<string | null>(null);
  const { status } = useSession();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<loginFormData>({
    defaultValues: {
      email: "",
      password: "",
    },
    resolver: zodResolver(loginFormData),
  });

  // Sometimes next-auth stores the error in the URL. Idk why.
  useEffect(() => {
    const error = router.query.error;
    if (!error) return;
    setCredentialsError(error.toString());
  }, [router.query.error]);

  async function onSubmit(data: loginFormData) {
    const callbackUrl = router.query.redirect?.toString() || "/";
    console.log(callbackUrl);
    const res = await signIn("credentials", {
      email: data.email,
      password: data.password,
      callbackUrl,
    });

    if (res?.error) {
      setCredentialsError(res.error);
      return;
    }

    if (res?.status === 200) await router.push("/");

    setCredentialsError(null);
  }

  if (status === "loading") {
    return <div className="loading loading-ring loading-lg" />;
  }

  return (
    <Layout title="Sign in">
      {credentialsError && (
        <div className="alert alert-error mb-4">{credentialsError}.</div>
      )}
      <form className=" flex flex-col gap-1" onSubmit={handleSubmit(onSubmit)}>
        <label htmlFor="email">Email</label>
        <input
          className="input border border-blue-500"
          type="text"
          {...register("email")}
        />
        {errors.email && <p>{errors.email.message}</p>}
        <label htmlFor="password">Password</label>
        <input
          className="input border border-blue-500"
          type="password"
          {...register("password")}
        />
        {errors.password && <p>{errors.password.message}</p>}
        <button className="btn-primary btn-block btn my-4" type="submit">
          Login
        </button>
      </form>
      <div className="text-center">
        <Link className="btn-secondary btn-block btn" href="/auth/signup">
          Or Register
        </Link>
      </div>
    </Layout>
  );
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  await trackEvent(ctx, EventNames.SIGN_IN_PAGE_VIEWED);
  return { props: {} };
};

export default SignIn;
